import { AfterViewInit, Component, ViewChild } from "@angular/core";
import { MatSort } from "@angular/material";
import { MdePopoverTrigger } from "@material-extended/mde";
import { from, merge, of as observableOf } from "rxjs";
import { catchError, map, startWith, switchMap, tap } from "rxjs/operators";
import {
  ListingSearchFilter,
  ListingSearchType,
} from "../../dashboard/interfaces/listing-search-filter";
import { ListingTabFilter } from "../../dashboard/interfaces/listing-tab-filter";
import { ListingBaseComponent } from "../../dashboard/listing-base.component";
import { AuthService } from "../../services/ajs-auth.service";
import { PerfilService } from "../../services/ajs-perfil.service";
import { UiRouterState } from "../../services/ajs-router-state.service";
import { StateParamsService } from "../../services/ajs-state-params.service";
import {
  QueryReturnPackagesRequest,
  QueryReturnPackagesResponseItem,
  ReturnPackageApiService,
} from "../../services/api/return-package.service";
import { TourService } from "../../tour/tour.service";
import { LocalStorageService } from "src/app/services/localstorage.service";
import { FinancialApiService as FinancialService } from "./../../services/financial.service";

const statusFilters = {
  finished: ["DevolvidaParaRemetente", "Devolvida"],
  unfinished: ["EmDevolucaoParaRemetente", "EmDevolucao"],
};

const displayColumns = {
  finished: [
    "original_tracking_code",
    "recipient",
    "returnEventDescription",
    "returnCarrierTrackingCode",
    "return_actual_delivery",
    "reference",
    "originalStatus",
  ],
  unfinished: [
    "original_tracking_code",
    "recipient",
    "returnEventDescription",
    "returnCarrierTrackingCode",
    "return_expected_delivery",
    "reference",
    "originalStatus",
  ],
};

@Component({
  selector: "mandae-listing",
  templateUrl: "./listing.component.html",
  styleUrls: ["./listing.component.scss"],
})
export class ListingComponent
  extends ListingBaseComponent<QueryReturnPackagesRequest>
  implements AfterViewInit {
  public currentFilter = "unfinished";
  public filters: ListingSearchFilter[] = [
    {
      value: "trackingCode",
      viewValue: "Código de rastreamento",
      displayType: ListingSearchType.Text,
      placeholder: "Insira o código de rastreamento",
    },
    {
      value: "carrierTrackingCode",
      viewValue: "Código de rastreamento da transportadora",
      displayType: ListingSearchType.Text,
      placeholder: "Insira o código de rastreamento da transportadora",
    },
    {
      value: "referenceCode",
      viewValue: "Código de referência",
      displayType: ListingSearchType.Text,
      placeholder: "Insira o código de referência",
    },
    {
      value: "recipientName",
      viewValue: "Nome do destinatário",
      displayType: ListingSearchType.Text,
      placeholder: "Insira o nome do destinatário",
    },
  ];

  public tabFilters: ListingTabFilter[] = [
    {
      displayValue: "Em devolução",
      value: "unfinished",
    },
    {
      displayValue: "Devolvidas",
      value: "finished",
    },
  ];

  public pages = 0;

  public displayedColumns = displayColumns.unfinished;

  public data: QueryReturnPackagesResponseItem[] = [];

  @ViewChild(MatSort) private sort: MatSort;
  @ViewChild(MdePopoverTrigger) private popoverTrigger: MdePopoverTrigger;

  public statusFilters = {
    EmDevolucaoParaRemetente: true,
    Devolvida: true,
    EmDevolucao: true,
  };

  constructor(
    public returnPackageApi: ReturnPackageApiService,
    private state: UiRouterState,
    private auth: AuthService,
    private profile: PerfilService,
    private tour: TourService,
    private stateParams: StateParamsService,
    private lss: LocalStorageService,
    private api: FinancialService
  ) {
    super();

    this.searchParameters = {
      limit: 10,
      offset: 0,
    };
  }

  ngAfterViewInit() {

    if (!this.sort) {
      return;
    }

    super.ngOnInit();

    this.sort.sortChange.subscribe(() => {
      this.searchParameters.order =
        (this.sort.direction === "asc" ? "" : "-") + this.sort.active;
      this.listing.currentPage = 1;
    });

    if (this.stateParams.tour) {
      this.startTour();
    }

    // TODO: remover isto quando utilizar o router do Angular
    from(this.profile.getBetaFeatures())
      .pipe(
        tap((features) => {
          const canAccess = features.funcionalidades.some(
            (feature) =>
              feature.codigo === "painel-devolucoes" && feature.possuiAcesso
          );

          if (!canAccess) {
            this.state.go("redirecionar");
          }
        })
      )
      .subscribe();

    this.returnPackageApi
      .getReturnPackages({
        status: <any>statusFilters.unfinished,
        limit: 1,
        offset: 0,
      })
      .subscribe((r) => (this.tabFilters[0].displayValue += ` (${r.count})`));

    merge(this.sort.sortChange, this.refresh)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.loading = true;
          return this.returnPackageApi.getReturnPackages(this.searchParameters);
        }),
        map((data) => {
          this.pages = Math.ceil(data.count / this.searchParameters.limit);
          this.loading = false;

          return data.data;
        }),
        catchError(() => {
          this.loading = false;
          return observableOf([]);
        }),
        tap((data) => (this.data = data))
      )
      .subscribe();
  }

  applySearch(data: any) {
    delete this.searchParameters.recipientName;
    delete this.searchParameters.referenceCode;
    delete this.searchParameters.trackingCode;
    delete this.searchParameters.carrierTrackingCode;

    if (data.searchBy) {
      this.searchParameters[data.searchBy] = data.value;
    }

    this.sort.sortChange.emit();
  }

  applyTabFilter(filter: string) {
    this.currentFilter = filter;
    this.resetStatusFilters();
    this.searchParameters.status = statusFilters[filter];
    this.displayedColumns = displayColumns[filter];

    if (this.sort) {
      this.sort.sortChange.emit();
    }
  }

  applyStatusFilters() {
    const status = [];

    for (const key in this.statusFilters) {
      if (this.statusFilters.hasOwnProperty(key)) {
        const element = this.statusFilters[key];
        if (element) {
          status.push(key);
        }
      }
    }

    this.searchParameters.status = status;
    this.popoverTrigger.closePopover();

    this.sort.sortChange.emit();
  }

  gotoTracking(code: string) {
    this.state.go("rastreamento", { numeroRastreio: code });
  }

  isEmptyDatasource() {
    return !this.data || this.data.length === 0;
  }

  public downloadRelatorio() {
    const user = this.lss.get("user");

    const keys = Object.keys(this.searchParameters);
    const filtro = [];

    for (let i = 0; i < keys.length; i++) {
      const temp = `${keys[i]}=${this.searchParameters[keys[i]]}`;
      filtro.push(temp);
    }

    this.api.downloadRelatorio(user.id, filtro[3], "devolucoes");
  }

  private resetStatusFilters() {
    this.statusFilters = {
      EmDevolucaoParaRemetente: true,
      Devolvida: true,
      EmDevolucao: true,
    };
  }

  private startTour() {
    const stepsConfig = [
      {
        title: "Área de devoluções",
        text:
          "Acompanhe as encomendas que estão em processo de devolução e as que já foram devolvidas.\n" +
          "Em caso de dúvidas, " +
          '<a href="https://mandaenuvemenvio.zendesk.com/hc/pt-br/articles/34308955581844-Painel-de-Devolu%C3%A7%C3%B5es" ' +
          'target="_blank">acesse ao FAQ</a>.\n',
        attachTarget: () =>
          document.querySelector("app-toolbar .mat-tab-link:nth-child(4)"),
        previous: {
          text: "Voltar",
          action: (ev, ref) => {
            ref.close("to_prev_section");
            this.state.go("reverse-list", { tour: true }, { inherit: false });
          },
        },
      },
      {
        title: "Encomendas em devolução",
        text:
          "Acompanhe as encomendas que estão em devolução para sua loja e entenda o motivo que levou à impossibilidade da entrega.",
        attachTarget: () =>
          document.querySelector(
            "mandae-dashboard-listing .mat-tab-link:nth-child(1)"
          ),
      },
      {
        title: "Encomendas devolvidas",
        text:
          "Confira o histórico de encomendas que foram devolvidas para sua loja.",
        attachTarget: () =>
          document.querySelector(
            "mandae-dashboard-listing .mat-tab-link:nth-child(2)"
          ),
      },
      {
        title: "Prazo de devolução",
        text:
          "Saiba qual a previsão de sua empresa receber os itens devolvidos.",
        attachTarget: () =>
          document.querySelector("th.mat-column-return_expected_delivery"),
        next: {
          text: "Ir para Indicadores",
          action: (ev, ref) => {
            ref.close("to_next_section");
            this.state.go("indicadores", { tour: true }, { inherit: false });
          },
        },
      },
    ];

    const steps = this.tour.makeTour(stepsConfig);

    this.tour.start(steps).subscribe();
  }
}
